/**
 * Vehicles sold tax-free as exports whose Bill of Lading hasn't arrived yet.
 *
 * The B/L is issued by the shipping line AFTER the sale, so an export deal legitimately closes
 * without one -- but the $0 sales tax is granted on the PROMISE that the vehicle leaves the
 * country. Until that Bill of Lading is on file, the dealership is the one on the hook: if the
 * shipment never happens, the state comes back for the tax.
 *
 * Today that liability lives in a text thread ("I should have it this week"). This is the screen
 * that stops it being forgotten.
 */
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';

interface PendingExportExemption {
  deal_id: string;
  deal_number: string;
  customer: string;
  vehicle: string;
  stock_number: string | null;
  deal_status: string;
  sale_price: number;
  tax_deposit_held: number;
  days_outstanding: number | null;
  created_at: string | null;
}

const currency = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

/** A B/L that's been outstanding for months is a different problem from one promised this week. */
const agingClass = (days: number | null): string => {
  if (days === null) return 'text-gray-500';
  if (days >= 90) return 'text-red-700 font-semibold';
  if (days >= 30) return 'text-amber-700 font-medium';
  return 'text-gray-700';
};

const PendingExportExemptions: React.FC = () => {
  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['pending-export-exemptions'],
    queryFn: () => apiService.get<PendingExportExemption[]>('/deals/export-exemptions/pending'),
  });

  if (isLoading) {
    return (
      <div className="card">
        <div className="h-6 w-64 bg-gray-200 rounded animate-pulse mb-4" />
        <div className="h-4 bg-gray-100 rounded animate-pulse" />
      </div>
    );
  }

  // Never render a silent empty box on a compliance surface -- "I see nothing" and "it failed to
  // load" must not look the same.
  if (isError) {
    return (
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Export Exemptions</h2>
        <p role="alert" className="text-sm text-red-600">
          Could not load pending export exemptions. Refresh to try again.
        </p>
      </div>
    );
  }

  const rows = data ?? [];
  const depositsHeld = rows.reduce((sum, r) => sum + (r.tax_deposit_held || 0), 0);
  const uncovered = rows.filter((r) => !r.tax_deposit_held);

  return (
    <div className="card">
      <div className="flex items-start justify-between mb-1">
        <h2 className="text-xl font-semibold text-gray-900">
          Export Exemptions — Awaiting Bill of Lading
        </h2>
        {rows.length > 0 && (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
            {rows.length} open
          </span>
        )}
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-gray-500 mt-2">
          Every export sale has its Bill of Lading on file. Nothing outstanding.
        </p>
      ) : (
        <>
          <p className="text-sm text-gray-600 mb-4">
            Sold tax-free on the promise of export. {currency(depositsHeld)} is being held on
            deposit against these — refund it when the Bill of Lading arrives, or apply it to the
            tax if the vehicle never ships.
          </p>

          {/* A deal with NO deposit is the dangerous one: the dealership is carrying that tax
              itself, with nothing but a promise. Call it out rather than burying it in a table. */}
          {uncovered.length > 0 && (
            <p role="alert" className="text-sm text-red-700 mb-4">
              <ExclamationTriangleIcon className="h-4 w-4 inline mr-1" aria-hidden="true" />
              {uncovered.length} {uncovered.length === 1 ? 'deal has' : 'deals have'} no tax deposit
              held — the dealership is carrying that tax with nothing to fall back on.
            </p>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                  <th className="py-2 pr-4">Deal</th>
                  <th className="py-2 pr-4">Vehicle</th>
                  <th className="py-2 pr-4">Buyer</th>
                  <th className="py-2 pr-4 text-right">Sale</th>
                  <th className="py-2 pr-4 text-right">Deposit held</th>
                  <th className="py-2 pr-4 text-right">Waiting</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.deal_id}
                    onClick={() => navigate(`/deals/${r.deal_id}`)}
                    className="border-b last:border-0 hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="py-2 pr-4 font-medium text-gray-900">{r.deal_number}</td>
                    <td className="py-2 pr-4 text-gray-700">
                      {r.vehicle || '—'}
                      {r.stock_number ? (
                        <span className="text-gray-400"> · #{r.stock_number}</span>
                      ) : null}
                    </td>
                    <td className="py-2 pr-4 text-gray-700">{r.customer || '—'}</td>
                    <td className="py-2 pr-4 text-right text-gray-900">{currency(r.sale_price)}</td>
                    <td className="py-2 pr-4 text-right">
                      {r.tax_deposit_held ? (
                        <span className="text-gray-900">{currency(r.tax_deposit_held)}</span>
                      ) : (
                        <span className="text-red-700 font-medium">none</span>
                      )}
                    </td>
                    <td className={`py-2 pr-4 text-right ${agingClass(r.days_outstanding)}`}>
                      {r.days_outstanding === null ? (
                        '—'
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          {r.days_outstanding >= 30 && (
                            <ExclamationTriangleIcon className="h-4 w-4" aria-hidden="true" />
                          )}
                          {r.days_outstanding} {r.days_outstanding === 1 ? 'day' : 'days'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default PendingExportExemptions;
